import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["children", "id", "component", "preventFocus", "mode", "onKeyDown", "disableCyclingFocus", "disableFocusOnDisabledItems", "disableTabIndex", "defaultActiveElementIndex", "focusActiveElementOnFocus", "focusActiveElementOnMount", "activeElement", "onActiveElementChange"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import PropTypes from "prop-types";
import React, { useEffect, useRef } from "react";
import { KEYBOARD_KEYS, KEYBOARD_NAVIGATOR_MODE as MODE } from "../../common/constants";
import { useControlOrUnControl, useUniqueId } from "../../common/hooks";
import { DATA_ATTRIBUTE_KEY, KeyboardNavigatorContext } from "./KeyboardNavigatorUtils";
/**
 * KeyboardNavigator is a React component that provides the capability to navigate over
 * its children with the keyboard arrow keys by wrapping the children that can be navigated with
 * the component `<KeyboardNavigatorItem>`.
 * Once the last child wrapped with `<KeyboardNavigatorItem>` is reached,
 * the focus will return to the first child.
 * To escape the loop, the tab key should be used.
 */

import { jsx as _jsx } from "@emotion/react/jsx-runtime";
const KeyboardNavigator = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
    children,
    id,
    component: Element,
    preventFocus,
    mode,
    onKeyDown,
    disableCyclingFocus,
    disableFocusOnDisabledItems,
    disableTabIndex,
    defaultActiveElementIndex,
    focusActiveElementOnFocus,
    focusActiveElementOnMount,
    activeElement,
    onActiveElementChange
  } = _ref,
      otherProps = _objectWithoutProperties(_ref, _excluded);

  // Internal array member to track all of the children KeyboardNavigatorItems
  const focusableElementsRef = useRef([]);
  const activeIndexesRef = useRef([]); // Internal array member to track the index values of all the active KeyboardNavigatorItems

  const activeIndexes = activeIndexesRef.current;
  const keyboardNavigatorId = useUniqueId({
    id,
    prefix: "KeyboardNavigator_"
  });
  let preventTriggeringChangeHandler = false;
  /**
   * useControlOrUnControl hook returns the getter and setter
   * methods for updating the prop value, renamed getPropValue to
   * getSelectedTab for sake of readability.
   */

  const [getActiveElement, setActiveElementPropValue] = useControlOrUnControl({
    unControlledPropValue: defaultActiveElementIndex,
    controlledPropValue: activeElement,
    changeHandler: onActiveElementChange
  });
  /**
   * Gets the activeElement value according to how the component is being used
   * Controlled from Props, Uncontrolled from state
   * @return {int}
   */

  const getActiveElementIndex = () => {
    const currentActiveElement = getActiveElement();
    let index;

    switch (typeof currentActiveElement) {
      case "string":
        index = currentActiveElement ? focusableElementsRef.current.indexOf(currentActiveElement) : -1;
        break;

      case "number":
        index = currentActiveElement;
        break;

      default:
        index = -1;
        break;
    }

    return index;
  };

  const prevActiveElementIndexRef = useRef(getActiveElementIndex());
  /**
   * Validates whether the activeElement and defaultActiveElementIndex are within range
   * @param {Boolean} isActiveElementUnmounted - Flag if active element exists in DOM or not.
   */

  const validateIndexesRanges = isActiveElementUnmounted => {
    const focusableElementsLength = focusableElementsRef.current.length;
    const defaultActiveElementIndexFromProps = focusableElementsLength > 0 ? defaultActiveElementIndex : -1;

    if (defaultActiveElementIndexFromProps < -1 || defaultActiveElementIndexFromProps >= focusableElementsLength) {
      // defaultActiveElementIndex is out of range, throw an error
      throw new Error(`defaultActiveElementIndex (${defaultActiveElementIndexFromProps}) is out of range`);
    }

    const activeIndex = getActiveElementIndex();

    if (activeIndex < -1 || activeIndex >= focusableElementsLength || isActiveElementUnmounted) {
      // activeElement is out of range, reset it to 0
      setActiveElementPropValue(0, true, focusableElementsRef.current[0], keyboardNavigatorId);
      prevActiveElementIndexRef.current = 0;
    }
  };
  /**
   * Changes the activeElementIndex value according to how the component is being used
   * This method covers the best approach to manage a component that needs to be uncontrolled and Controlled
   * depending on how it's used
   * @param  {int} index The new index of the `<KeyboardNavigatorItem>` to focus
   */


  const changeActiveIndex = index => {
    validateIndexesRanges();
    prevActiveElementIndexRef.current = getActiveElementIndex();
    setActiveElementPropValue(index, true, focusableElementsRef.current[index], keyboardNavigatorId);
  };
  /**
   * Context method that specifies if a `<KeyboardNavigatorItem>` should be focused
   * @param  {string} elementId The id of the element to be focused
   * @return {bool} Whether the elementId should be focused
   */


  const shouldFocusElement = elementId => focusableElementsRef.current[getActiveElementIndex()] === elementId;
  /**
   * Context method that specifies the active `<KeyboardNavigatorItem>` should be focused on mount
   * @param  {string} elementId The id of the element to be focused
   * @return {bool} Whether the elementId should be focused
   */


  const shouldFocusActiveElementOnMount = elementId => focusActiveElementOnMount && focusableElementsRef.current[getActiveElementIndex()] === elementId;
  /**
   * Context method that specifies if a `<KeyboardNavigatorItem>` should have tabindex="0"
   * @param  {string} elementId The id of the element to check
   * @return {bool} Whether the elementId should be set with tabindex="0"
   */


  const shouldSetTabIndex = elementId => shouldFocusElement(elementId) || getActiveElementIndex() === -1 && focusableElementsRef.current[defaultActiveElementIndex] === elementId;
  /**
   * Context method that adds the `<KeyboardNavigatorItem>` children to an internal
   * array so they can be tracked and managed accordingly no matter how deep in the
   * DOM tree they are
   */


  const addFocusableElement = (elementId, isDisabled) => {
    if (!(isDisabled && disableFocusOnDisabledItems) && focusableElementsRef.current.indexOf(elementId) === -1) {
      focusableElementsRef.current.push(elementId);
    }

    const index = focusableElementsRef.current.indexOf(elementId);

    if (!isDisabled && activeIndexes.indexOf(index) === -1) {
      activeIndexes.push(index); // Sort after inserting an item to maintain the ordered list of indexes for navigation

      activeIndexes.sort(function (a, b) {
        return a - b;
      });
    }
  };
  /**
   * Context method that removes the `<KeyboardNavigatorItem>` children from the internal
   * array that tracks them.
   * DOM tree they are
   */


  const removeFocusableElement = elementId => {
    const index = focusableElementsRef.current.indexOf(elementId);

    if (activeIndexes.indexOf(index) !== -1) {
      activeIndexes.splice(activeIndexes.indexOf(index), 1);
    }
  };
  /**
   * updateActiveIndexes method adjust the activeIndexes array.
   * Let's say:
   * activeElement - 2
   * focusableElementsRef - [firstItem, unMountedItem, thirdItem]
   * activeIndexes before this method - [0, 2]
   * after executing this method activeIndexes will be - [0, 1]
   * As unMountedItem element in the `focusableElementsRef` array
   * will be removed. Hence we are adjusting the other elements index value by subtracting
   * with 1 for every call of this method.
   *
   * @param {Number} indexPostion
   */


  const updateActiveIndexes = indexPostion => {
    const startIndex = indexPostion + 1;
    const currentActiveElementIndex = getActiveElementIndex();
    const activeIndexesLength = activeIndexes.length;

    for (let elementPosition = startIndex; elementPosition < activeIndexesLength; elementPosition++) {
      activeIndexes[elementPosition] = activeIndexes[elementPosition] - 1;
    }
    /**
     * when deleted element position is less than current activeElement, then
     * activeElementIndex also needs to be adjusted by subtracting the number
     * of elements removed. Hence subtracting by 1.
     *
     * To avoid the focusing of elements when elements are deleted because of external event
     * setting the `prevActiveElementIndexRef` value as well.
     */


    if (indexPostion < currentActiveElementIndex) {
      const updatedActiveIndex = currentActiveElementIndex - 1;
      setActiveElementPropValue(updatedActiveIndex, true);
      prevActiveElementIndexRef.current = currentActiveElementIndex - 1;
      preventTriggeringChangeHandler = true;
    }
  };
  /**
   * Updates the state indicating the next item that should have focus
   * @param  {Integer} Where to move the index of the active child
   */


  const moveActiveChildIndexBy = delta => {
    const numChildren = activeIndexes.length;
    const currentActiveElementIndex = getActiveElementIndex();
    let nextIndex = activeIndexes.indexOf(currentActiveElementIndex) + delta;

    if (nextIndex >= numChildren) {
      // loop
      nextIndex = disableCyclingFocus ? numChildren - 1 : 0;
    } else if (nextIndex < 0) {
      // reverse loop
      nextIndex = disableCyclingFocus ? 0 : numChildren - 1;
    }

    changeActiveIndex(activeIndexes[nextIndex]);
  };

  const contextValue = {
    addFocusableElement,
    removeFocusableElement,
    shouldSetTabIndex,
    disableTabIndex,
    shouldFocusElement,
    shouldFocusActiveElementOnMount,
    getActiveElementIndex,
    preventFocus
  };

  const handleKeyDown = event => {
    const eventTarget = event.target;
    const isModeBoth = mode === MODE.BOTH;
    const isModeVertical = mode === MODE.VERTICAL || isModeBoth;
    const isModeHorizontal = mode === MODE.HORIZONTAL || isModeBoth;
    /**
     * Check if the eventTarget element is a child of this component.
     * This avoids changing the wrong component `activeElement`
     * when the KeyboardNavigator is used nested inside another KeyboardNavigator
     */

    if (focusableElementsRef.current.indexOf(eventTarget.getAttribute(DATA_ATTRIBUTE_KEY)) >= 0) {
      const eventKey = event.key;

      if (eventKey === KEYBOARD_KEYS.UP && isModeVertical) {
        event.preventDefault();
        moveActiveChildIndexBy(-1);
      } else if (eventKey === KEYBOARD_KEYS.DOWN && isModeVertical) {
        event.preventDefault();
        moveActiveChildIndexBy(1);
      } else if (eventKey === KEYBOARD_KEYS.LEFT && isModeHorizontal) {
        event.preventDefault();
        moveActiveChildIndexBy(-1);
      } else if (eventKey === KEYBOARD_KEYS.RIGHT && isModeHorizontal) {
        event.preventDefault();
        moveActiveChildIndexBy(1);
      }

      if (onKeyDown) {
        onKeyDown(event);
      }
    }
  };

  const handleFocus = _ref2 => {
    let {
      target
    } = _ref2;

    /**
     * if the eventTarget has the DATA_ATTRIBUTE_KEY
     * means the focus event came from a `KeyboardNavigatorItem`
     */
    if (target.hasAttribute(DATA_ATTRIBUTE_KEY) && !target.hasAttribute("disabled")) {
      const keyboardNavigatorItem = target.getAttribute(DATA_ATTRIBUTE_KEY);
      /**
       * Check if the eventTarget element is a child of this component.
       * This avoids changing the wrong component `activeElement`
       * when the KeyboardNavigator is used nested inside another KeyboardNavigator
       */

      if (focusableElementsRef.current.indexOf(keyboardNavigatorItem) > -1) {
        /**
         * Get the index of the keyboardNavigatorItem in the
         * internal array that tracks the keyboardNavigatorItem children
         */
        const currentActiveElement = focusableElementsRef.current.indexOf(keyboardNavigatorItem);
        /**
         * If the index of the keyboardNavigatorItem is different
         * than the one we have currently have active, we change it.
         */

        if (currentActiveElement !== getActiveElementIndex()) {
          changeActiveIndex(currentActiveElement);
        }
      }
    } else if (focusActiveElementOnFocus) {
      const activeIndex = getActiveElementIndex();
      changeActiveIndex(activeIndex);
      const activeElementId = focusableElementsRef.current[activeIndex];
      const activeElement = document.querySelector(`[${DATA_ATTRIBUTE_KEY}=${activeElementId}]`);
      typeof (activeElement === null || activeElement === void 0 ? void 0 : activeElement.focus) === "function" && activeElement.focus();
    }
  };
  /**
   * When elements are added and removed because of external event then
   * elements index position in `focusableElements` array changes, hence
   * updated prevActiveElementIndexRef as well. Doing so will avoid focusing
   * on KeyboarNavigatorItems when component update happens because of external
   * change events.
   */


  const handleBlur = () => {
    prevActiveElementIndexRef.current = getActiveElementIndex();
  };

  useEffect(() => {
    const childrenAsArray = Array.prototype.slice.call(document.getElementById(keyboardNavigatorId).children);
    /**
     * When `KeyboardNavigatorItem` elements are added dynamically then elements
     * order is unknown. Hence we rely on the DOM for reordering the `focusableElementsRef`
     * array based on the DOM. Capturing the `KeyboardNavigatorItem` id either from
     * direct child or its children element.
     */

    const domBasedFocusableElements = childrenAsArray.map(eachNavItem => {
      var _eachNavItem$querySel;

      return eachNavItem.dataset.focusKey || ((_eachNavItem$querySel = eachNavItem.querySelectorAll(`[${DATA_ATTRIBUTE_KEY}]`)[0]) === null || _eachNavItem$querySel === void 0 ? void 0 : _eachNavItem$querySel.dataset.focusKey);
    }).filter(eachId => eachId);
    /**
     * if an active element is focused and because of external event the `NavigatorItem`
     * is deleted then we need to move focus to first element. Need to do this check
     * before cleaning up the `focusableElementsRef`.
     */

    const activeIndex = getActiveElementIndex();
    const isActiveElementUnmounted = activeIndex !== -1 && activeIndex !== "" && domBasedFocusableElements.indexOf(focusableElementsRef.current[activeIndex]) === -1;
    /**
     * `focusableElementsRef` has to be amended when any of the elementIds are not found in the
     * DOM. Hence caching it so that when other elements are modified
     * elements index position is not altered.
     */

    const cachedFocusableElements = [...focusableElementsRef.current];
    /**
     * If an element existing in `focusableElementsRef` array and does not exists
     * in the `DOM`, meaning that element has been intialized and removed immediately
     * by parent component(probably in useEffect phase Eg: NavBar component).
     * In such cases that particular element has to be removed from `focusableElementsRef`
     * array and below loop does the same.
     *
     * else if does reordering of elements in `focusableElementsRef` array based
     * on DOM elements index position.
     */

    cachedFocusableElements.forEach((eachId, elementPosition) => {
      const domBasedElementPosition = domBasedFocusableElements.indexOf(eachId);
      const updatedIndex = focusableElementsRef.current.indexOf(eachId);

      if (domBasedElementPosition === -1) {
        // update the other indexes by subtracting 1
        updateActiveIndexes(updatedIndex); // removed from `activeIndexes` array;

        removeFocusableElement(eachId);
        focusableElementsRef.current.splice(updatedIndex, 1);
      } else if (domBasedElementPosition !== updatedIndex) {
        const elementIdToSwapWith = focusableElementsRef.current[domBasedElementPosition];
        const slicedElementId = focusableElementsRef.current.splice(elementPosition, 1, elementIdToSwapWith)[0];
        focusableElementsRef.current[domBasedElementPosition] = slicedElementId;
      }
    });
    /**
     * When `KeyboardNavigator` is updating the `activeElementIndex` value
     * because of external events then we don't need to validate the index
     * ranges hence preventing it.
     */

    !preventTriggeringChangeHandler && validateIndexesRanges(isActiveElementUnmounted);
  });
  return _jsx(KeyboardNavigatorContext.Provider, {
    value: _objectSpread(_objectSpread({}, contextValue), {}, {
      preventFocus,
      prevActiveElementIndex: prevActiveElementIndexRef.current
    }),
    children: _jsx(Element, _objectSpread(_objectSpread({}, otherProps), {}, {
      id: keyboardNavigatorId,
      ref: ref,
      onFocus: handleFocus,
      onBlur: handleBlur,
      onKeyDown: handleKeyDown,
      children: children
    }))
  });
});
KeyboardNavigator.propTypes = process.env.NODE_ENV !== "production" ? {
  /** These are navigable elements */
  children: PropTypes.node.isRequired,

  /**
   * Indicates the index or Id of the `KeyboardNavigatorItem` that should be focused.
   * When using this prop, the component will behave as a __Controlled__ component
   * and you also need the prop `onActiveElementChange` to sync the `activeElement` back.
   *
   * Its value should be between -1 and the number of `KeyboardNavigatorItem`
   * children minus 1 when used as index.
   * The value __-1__ and/or `''` represents __no active__ element.
   */
  activeElement: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /** Any valid HTML tag name or a React component factory */
  component: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),

  /**
   * Indicates the index of the `KeyboardNavigatorItem` that should be focused
   * when tab'd in
   *
   * Its value should be between -1 and the number of `KeyboardNavigatorItem` children minus 1.
   * The value __-1__ represents __no active__ element.
   */
  defaultActiveElementIndex: PropTypes.number,

  /**
   * It disables cycling focus behavior.
   */
  disableCyclingFocus: PropTypes.bool,

  /**
   * It disables the focus for disabled items.
   */
  disableFocusOnDisabledItems: PropTypes.bool,

  /**
   * It disables the usage of tabIndex by the component.
   * Special property only for framework usage
   */
  disableTabIndex: PropTypes.bool,

  /**
   * Flag to indicate if active `KeyboardNavigatorItem` should focus when the
   * KeyboardNavigator is focused.
   */
  focusActiveElementOnFocus: PropTypes.bool,

  /**
   * Flag to indicate if active `KeyboardNavigatorItem` should focus when mount.
   * Useful when `KeyboardNavigator` needs to focus first child when showing
   * in `Ballon` for example
   * Special property only for framework usage
   */
  focusActiveElementOnMount: PropTypes.bool,

  /** The component id value. While optional, if an id is not added, one will be generated. */
  id: PropTypes.string,

  /**
   * controls which arrow key events are captured to move active focus within the list:
   *
   * Mode                                 | Keys
   * ----                                 | ----
   * `BOTH`       | ⬅️ ➡️ ⬆️ ⬇️
   * `HORIZONTAL` | ⬅️ ➡️
   * `VERTICAL`   | ⬆️ ⬇️
   *
   * _Note: focus loops when arrowing past one of the boundaries;_
   * _tabbing moves the user away from the list._
   */
  mode: PropTypes.oneOf([MODE.BOTH, MODE.HORIZONTAL, MODE.VERTICAL]),

  /**
   * Callback triggered when the `KeyboardNavigator` changes the children focus.
   * This function receives 3 parameters:
   * param 1: index of the new activeElement being selected,
   * param 2: id of the `KeyboardNavigatorItem` being selected
   * param 3: id of this `KeyboardNavigator` instance.
   */
  onActiveElementChange: PropTypes.func,

  /** Handler for `component` element `onKeyDown` event. */
  onKeyDown: PropTypes.func,

  /**
   * Prop to control the focus of active element from parent component.
   */
  preventFocus: PropTypes.bool
} : {};
KeyboardNavigator.defaultProps = {
  id: null,
  preventFocus: false,
  component: "div",
  activeElement: null,
  defaultActiveElementIndex: 0,
  mode: MODE.BOTH,
  disableTabIndex: false,
  onActiveElementChange: null,
  onKeyDown: null,
  focusActiveElementOnFocus: false,
  focusActiveElementOnMount: false,
  disableCyclingFocus: false,
  disableFocusOnDisabledItems: false
};
KeyboardNavigator.displayName = "KeyboardNavigator";
export default KeyboardNavigator;